import { DateTime } from "luxon";

/**
 * Résout les ids de statut "actifs" (à traiter + en cours) depuis LEAD_STATUS
 * (données runtime). Ids confirmés contre l'API du BO : "À traiter" = TO_DO,
 * "En cours" = IN_PROGRESS. On garde une résolution par libellé en repli au cas
 * où la config d'un autre BO diffère, et un fallback final sur IN_PROGRESS seul.
 */
export const resolveInProgressStatusIds = (LEAD_STATUS = []) => {
  const ids = [];

  const inProgress =
    LEAD_STATUS.find((s) => s.id === "IN_PROGRESS") ||
    LEAD_STATUS.find((s) => /en\s*cours/i.test(s.name || ""));
  if (inProgress) ids.push(inProgress.id);

  const toDo =
    LEAD_STATUS.find((s) => s.id === "TO_DO") ||
    LEAD_STATUS.find(
      (s) =>
        s.id !== (inProgress && inProgress.id) &&
        /traiter|à\s*traiter|nouveau|new/i.test(s.name || ""),
    );
  if (toDo) ids.unshift(toDo.id);

  // Fallback : si rien trouvé, ne renvoyer que IN_PROGRESS (jamais un id erroné).
  return ids.length ? ids : inProgress ? [inProgress.id] : [];
};

/**
 * Définition d'un preset : id, label, et builder qui renvoie un PATCH partiel
 * des champs de filtres pilotés par ce preset.
 * ctx = { userId, myRole, LEAD_STATUS }
 */
export const LEAD_PRESETS = [
  {
    id: "MY_IN_PROGRESS",
    label: "Mes leads en cours",
    icon: "fa-bullseye",
    isAvailable: (ctx) => !!ctx.userId && ctx.myRole !== "PARTNER",
    build: (ctx) => ({
      assignedTo: ctx.userId ? [ctx.userId] : [],
      statuses: resolveInProgressStatusIds(ctx.LEAD_STATUS),
    }),
  },
  {
    id: "LAST_30_DAYS",
    label: "30 derniers jours",
    icon: "fa-calendar",
    isAvailable: () => true,
    build: () => ({
      beginAt: DateTime.now().minus({ days: 30 }).endOf("day").toISO(),
      endAt: DateTime.now().toISO(),
    }),
  },
];

/** Raccourcis de période pour le tiroir. */
export const PERIOD_SHORTCUTS = [
  {
    id: "LAST_30_DAYS",
    label: "30 derniers jours",
    build: () => ({
      beginAt: DateTime.now().minus({ days: 30 }).endOf("day").toISO(),
      endAt: DateTime.now().toISO(),
    }),
  },
  {
    id: "LAST_7_DAYS",
    label: "7 derniers jours",
    build: () => ({
      beginAt: DateTime.now().minus({ days: 7 }).endOf("day").toISO(),
      endAt: DateTime.now().toISO(),
    }),
  },
  {
    id: "THIS_MONTH",
    label: "Ce mois-ci",
    build: () => ({
      beginAt: DateTime.now().startOf("month").toISO(),
      endAt: DateTime.now().toISO(),
    }),
  },
];

/**
 * Un preset est "actif" si l'état courant des filtres pilotés correspond
 * exactement à ce que le preset produit. Comparaison ordre-insensible
 * pour les tableaux.
 */
const sameSet = (a = [], b = []) =>
  a.length === b.length && [...a].sort().join("|") === [...b].sort().join("|");

export const isPresetActive = (preset, filters, ctx) => {
  const patch = preset.build(ctx);
  return Object.entries(patch).every(([key, val]) => {
    if (Array.isArray(val)) return sameSet(filters[key] || [], val);
    return (filters[key] || "") === (val || "");
  });
};
